import {
  Component,
  computed,
  EventEmitter,
  inject,
  Output,
  signal,
  ChangeDetectionStrategy,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectChange, MatSelectModule } from "@angular/material/select";
import { ActivatedRoute, Router } from "@angular/router";
import { IconComponent } from "@claas/claas-ui-components";
import { ToggleComponent } from "@claas/claas-ui-components/form/toggle";
import { TranslatePipe } from "@ngx-translate/core";
import { map } from "rxjs";
import { SelectKeyValue } from "../../models/select-key-value";
import { DocumentIdStorageService } from "../../services/document-id-storage.service";
import { DocumentLanguagesStorageService } from "../../services/document-languages-storage.service";
import { WebStorage } from "../../services/web-storage";

@Component({
  selector: "claas-document-settings-modal",
  imports: [
    IconComponent,
    TranslatePipe,
    MatFormFieldModule,
    MatSelectModule,
    ToggleComponent,
  ],
  templateUrl: "./document-settings-modal.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./document-settings-modal.scss",
})
export class DocumentSettingsModal {
  private documentLanguageStorageService = inject(
    DocumentLanguagesStorageService,
  );
  private documentIdStorageService = inject(DocumentIdStorageService);
  private storage = inject(WebStorage);
  private route = inject(ActivatedRoute);
  router = inject(Router);

  @Output() closeSettings = new EventEmitter<void>();

  isDocumentIDOn = toSignal(this.documentIdStorageService.documentID$);
  data = toSignal(this.route.data);
  primaryDocumentLanguagesIds = toSignal(
    this.documentLanguageStorageService.primaryDocumentLanguages$,
  );
  secondaryDocumentLanguagesIds = toSignal(
    this.documentLanguageStorageService.secondaryDocumentLanguages$,
  );
  countryID = toSignal(
    this.route.paramMap.pipe(
      map((params) =>
        params.get("country")?.toLowerCase()
          ? encodeURIComponent(params.get("country")?.toLowerCase() as string)
          : "",
      ),
    ),
    {
      initialValue: "",
    },
  );
  languageID = toSignal(
    this.route.paramMap.pipe(
      map((params) =>
        params.get("language")
          ? encodeURIComponent(params.get("language")?.toLowerCase() as string)
          : "",
      ),
    ),
    {
      initialValue: "",
    },
  );
  yearID = toSignal(
    this.route.paramMap.pipe(
      map((params) =>
        params.get("year")
          ? encodeURIComponent(params.get("year")?.toLowerCase() as string)
          : "",
      ),
    ),
    {
      initialValue: "",
    },
  );
  primaryLanguageOptions = computed(() => {
    return this.data()?.["documentLanguages"]?.primary;
  });
  secondaryLanguageOptions = computed(
    () => this.data()?.["documentLanguages"]?.secondary,
  );
  primarySelectedLanguageOptions = computed(() => {
    // use the previously selected documentLanguages for the current country from the storage
    if (this.primaryDocumentLanguagesIds()?.[this.countryID()]?.length) {
      return this.primaryDocumentLanguagesIds()?.[this.countryID()];
    }

    // if not available use the current language
    const documentLanguageIds = this.primaryLanguageOptions()?.map(
      (documentLanguage: SelectKeyValue) => documentLanguage.key,
    );

    const defaultDocumentLanguageByLanguageParam = documentLanguageIds?.find(
      (id: string) => id === this.languageID(),
    );

    if (defaultDocumentLanguageByLanguageParam) {
      return [defaultDocumentLanguageByLanguageParam];
    } else {
      // if documentLanguage for current language not available use the first available primary  document language
      return documentLanguageIds[0] ? [documentLanguageIds[0]] : [];
    }
  });
  secondarySelectedLanguageOptions = computed(() => {
    return this.secondaryDocumentLanguagesIds()?.[this.countryID()] ?? [];
  });

  isToggleDocumentIdChecked = signal(false);
  isChecked = computed(() => this.isDocumentIDOn() as boolean);

  @Output()
  showDocumentID = new EventEmitter<boolean>();

  isOnlySelected(key: string): boolean {
    const selected = this.primarySelectedLanguageOptions() ?? [];
    return selected.length === 1 && selected[0] === key;
  }

  handleSettingsCloseClick() {
    this.closeSettings.emit();
  }

  handlePrimaryLanguageSelectChange(evt: MatSelectChange) {
    const selected = evt.value;
    // Prevent deselecting the last remaining item
    if (
      selected.length === 0 &&
      this.primarySelectedLanguageOptions()?.length === 1
    ) {
      return;
    }

    const current = this.primaryDocumentLanguagesIds() ?? {};
    const updated = { ...current, [this.countryID()]: selected };
    this.documentLanguageStorageService.setPrimaryDocumentLanguages(updated);
  }

  handleSecondaryLanguageSelectChange(evt: MatSelectChange) {
    const selected = evt.value;
    const current = this.secondaryDocumentLanguagesIds() ?? {};
    const updated = { ...current, [this.countryID()]: selected };
    this.documentLanguageStorageService.setSecondaryDocumentLanguages(updated);
  }

  navigateTo() {
    this.router.navigateByUrl(
      `/pia/${this.countryID()}/${this.languageID()}/${this.yearID()}/contacts`,
    );
  }

  toggleDocumentID(value: boolean) {
    this.storage.documentID = value;
    this.isToggleDocumentIdChecked.set(value);
    this.showDocumentID.emit(this.isToggleDocumentIdChecked());
    this.documentIdStorageService.setDocumentId(value);
  }
}
